import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Input,
  FormControl,
  InputLabel
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { CheckInFleetBookingPayload } from "../../../hooks/Remote/Fleet/useRemoteCheckInFleetBooking";
import { NumericFormatCustom } from "../../Forms/checkin-radius-numericFormat.component";

export type CheckInFormValues = {
  odometer: string;
  conditionNotes: string;
  photos: File[];
};

type Props = {
  open: boolean;
  bookingId: number | null;
  initialOdometer?: number | null;
  mode?: "handover" | "return";
  onClose: () => void;
  onSubmit: (payload: Omit<CheckInFleetBookingPayload, "bookingId">) => Promise<void>;
  isSubmitting?: boolean;
};

const emptyForm: CheckInFormValues = {
  odometer: "",
  conditionNotes: "",
  photos: []
};

export function CheckInDialog({
  open,
  bookingId,
  initialOdometer = null,
  mode = "handover",
  onClose,
  onSubmit,
  isSubmitting = false
}: Readonly<Props>) {
  const { t } = useTranslation();

  const [form, setForm] = useState<CheckInFormValues>(() => ({
    ...emptyForm,
    odometer: initialOdometer == null ? "" : String(initialOdometer)
  }));

  const handleClose = () => {
    setForm({
      ...emptyForm,
      odometer: initialOdometer == null ? "" : String(initialOdometer)
    });
    onClose();
  };

  const handleSubmit = async () => {
    if (bookingId == null) return;

    const trimmed = form.odometer.trim();
    const odometer = trimmed === "" ? null : Number(trimmed);
    const trimmedConditionNotes = form.conditionNotes.trim();
    const conditionNotes = trimmedConditionNotes === "" ? null : trimmedConditionNotes;
    const photos = form.photos.length > 0 ? form.photos : undefined;

    await onSubmit({ odometer, conditionNotes, photos })
      .then(() => handleClose())
      .catch(() => {});
  };

  // When dialog opens or initialOdometer changes, reset form with latest odometer
  useEffect(() => {
    if (!open) return;
    setForm({
      ...emptyForm,
      odometer: initialOdometer == null ? "" : String(initialOdometer)
    });
  }, [open, initialOdometer]);

  const handleOdometerChange = (raw: number | null) => {
    setForm(prev => ({ ...prev, odometer: raw == null ? "" : String(raw) }));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === "return" ? t("fleet_returnProtocol") : t("fleet_handoverProtocol")}
      </DialogTitle>
      <DialogContent sx={{ overflowX: "hidden" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <FormControl fullWidth variant="standard">
            <InputLabel htmlFor="odometer-input">{t("Odometer")}</InputLabel>
            <Input
              id="odometer-input"
              inputProps={{ "aria-label": t("Odometer") }}
              data-testid="odometer-input"
              value={form.odometer}
              onChange={e => handleOdometerChange(Number(e.target.value))}
              inputComponent={NumericFormatCustom as any} // type="number"
              error={!form.odometer}
              slotProps={{ input: { min: 0, step: 1 } }}
            />
          </FormControl>

          <TextField
            label={t("Notes")}
            multiline
            rows={3}
            value={form.conditionNotes}
            onChange={e => setForm(prev => ({ ...prev, conditionNotes: e.target.value }))}
            fullWidth
            variant="outlined"
            slotProps={{ input: { "data-testid": "condition-notes-input", "aria-label": "Notes" } }}
          />

          <Button variant="outlined" component="label" data-testid="add-photos-btn">
            {t("Add photos")}
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={e => {
                const files = e.target.files ? Array.from(e.target.files) : [];
                setForm(prev => ({ ...prev, photos: [...prev.photos, ...files] }));
              }}
            />
          </Button>

          {form.photos.length > 0 && (
            <Box sx={{ fontSize: "body2", color: "text.secondary", pl: 1 }}>
              {form.photos.length}{" "}
              {form.photos.length > 1 ? t("files selected") : t("file selected")}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          data-testid="fleet-booking-event-checkin-dialog-cancel-btn"
          onClick={handleClose}
          disabled={isSubmitting}
          color="secondary"
        >
          {t("Cancel")}
        </Button>

        <Button
          data-testid="fleet-booking-event-checkin-dialog-submit-btn"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? t("Submitting…") : t("Submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
